// ABOUTME: Hook for managing workspace members via tRPC queries and mutations.
// ABOUTME: Provides member list, remove, and role update functionality.

import { useCallback } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { trpc } from '@/lib/trpc';
import type { WorkspaceRole } from '@/types/workspace';

export function useWorkspaceMembers() {
    const { currentWorkspace, isAdmin } = useWorkspace();
    const workspaceId = currentWorkspace?.id;
    const utils = trpc.useUtils();

    const membersQuery = trpc.workspaceMembers.list.useQuery(
        { workspaceId: workspaceId! },
        {
            enabled: !!workspaceId,
            select: (data) => ((data as any).members ?? data),
        }
    );

    const removeMutation = trpc.workspaceMembers.remove.useMutation({
        onSuccess: () => {
            utils.workspaceMembers.list.invalidate();
        },
    });

    const updateRoleMutation = trpc.workspaceMembers.updateRole.useMutation({
        onSuccess: () => {
            utils.workspaceMembers.list.invalidate();
        },
    });

    const removeMember = useCallback(async (userId: string) => {
        if (!workspaceId || !isAdmin) return false;
        try {
            await removeMutation.mutateAsync({ workspaceId, userId });
            return true;
        } catch (err) {
            console.error('Failed to remove member:', err);
            return false;
        }
    }, [workspaceId, isAdmin, removeMutation]);

    const updateRole = useCallback(async (userId: string, role: WorkspaceRole) => {
        if (!workspaceId || !isAdmin) return false;
        try {
            await updateRoleMutation.mutateAsync({ workspaceId, userId, role });
            return true;
        } catch (err) {
            console.error('Failed to update role:', err);
            return false;
        }
    }, [workspaceId, isAdmin, updateRoleMutation]);

    const members: any[] = membersQuery.data ?? [];

    return {
        members,
        loading: membersQuery.isLoading,
        error: membersQuery.error?.message ?? removeMutation.error?.message ?? updateRoleMutation.error?.message ?? null,
        refetch: () => { utils.workspaceMembers.list.invalidate(); },
        removeMember,
        updateRole,
    };
}
